import React, { useMemo } from 'react';
import type { CheckedState } from '@radix-ui/react-checkbox';
import { Checkbox } from '../ui/checkbox';
import type { TargetProfileOption } from '../../services/apiService';

interface TargetProfileSelectorProps {
  title: string;
  options: TargetProfileOption[];
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
  disabled?: boolean;
  loading?: boolean;
  error?: string | null;
}

function collectDescendantIds(option: TargetProfileOption): number[] {
  const ids: number[] = [option.id];
  for (const child of option.children ?? []) {
    ids.push(...collectDescendantIds(child));
  }
  return ids;
}

function hasSelectedDescendants(option: TargetProfileOption, selected: Set<number>): boolean {
  for (const child of option.children ?? []) {
    if (selected.has(child.id) || hasSelectedDescendants(child, selected)) {
      return true;
    }
  }
  return false;
}

export function TargetProfileSelector({
  title,
  options,
  selectedIds,
  onSelectionChange,
  disabled,
  loading,
  error
}: TargetProfileSelectorProps) {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const handleToggle = (option: TargetProfileOption, state: CheckedState) => {
    const shouldSelect = state === true;
    const descendants = collectDescendantIds(option);
    const updated = new Set(selectedIds);

    if (shouldSelect) {
      for (const id of descendants) {
        updated.add(id);
      }
    } else {
      for (const id of descendants) {
        updated.delete(id);
      }
    }

    const ordered = Array.from(updated).sort((a, b) => a - b);
    onSelectionChange(ordered);
  };

  const renderOption = (option: TargetProfileOption, depth = 0) => {
    const isChecked = selectedSet.has(option.id);
    const hasDescendant = hasSelectedDescendants(option, selectedSet);
    const checkedState: CheckedState = isChecked ? true : hasDescendant ? 'indeterminate' : false;

    return (
      <div key={option.id} className="space-y-2">
        <div className="flex items-start gap-3" style={{ marginLeft: depth * 16 }}>
          <Checkbox
            checked={checkedState}
            onCheckedChange={(state) => handleToggle(option, state)}
            disabled={disabled}
          />
          <div>
            <div className="text-sm font-medium text-gray-800">{option.name}</div>
            {option.description && (
              <div className="text-xs text-gray-600 whitespace-pre-line">{option.description}</div>
            )}
          </div>
        </div>
        {option.children && option.children.length > 0 && (
          <div className="space-y-2">
            {option.children.map(child => renderOption(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      </div>
      <div className="border rounded-md bg-white p-3 max-h-72 overflow-y-auto">
        {loading && (
          <div className="text-sm text-gray-500">Loading target profiles...</div>
        )}
        {!loading && error && (
          <div className="text-sm text-red-600">{error}</div>
        )}
        {!loading && !error && options.length === 0 && (
          <div className="text-sm text-gray-500">No options available.</div>
        )}
        {!loading && !error && options.length > 0 && (
          <div className="space-y-2">
            {options.map(option => renderOption(option))}
          </div>
        )}
      </div>
    </div>
  );
}
